import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tree,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Typography,
  Row,
  Col,
  Tabs,
  Transfer,
  Checkbox,
  Badge
} from 'antd';
import {
  SettingOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  TeamOutlined,
  SecurityScanOutlined,
  UserOutlined,
  SearchOutlined
} from '@ant-design/icons';
import type { DataNode } from 'antd/es/tree';
import type { ColumnsType } from 'antd/es/table';
import type { TransferItem } from 'antd/es/transfer';

import { UserService } from '../../../services/userService';
import { useAuth } from '../../../contexts/AuthContext';
import type { PermissionGroup, Program, User, UserPermission } from '../../../types/user';
import { PERMISSION_ACTIONS } from '../../../types/user';

const { Title, Text } = Typography;

interface PermissionState {
  permissionGroups: PermissionGroup[];
  programs: Program[];
  users: User[];
  selectedGroup: PermissionGroup | null;
  groupMembers: string[];
  groupPermissions: UserPermission[];
  loading: boolean;
  modalVisible: boolean;
  modalType: 'group' | 'permissions' | 'members';
  editingGroup: PermissionGroup | null;
}

const PermissionManagement: React.FC = () => {
  const [form] = Form.useForm();
  const { refreshPermissions } = useAuth();
  const [permSearchText, setPermSearchText] = useState('');

  const [state, setState] = useState<PermissionState>({
    permissionGroups: [],
    programs: [],
    users: [],
    selectedGroup: null,
    groupMembers: [],
    groupPermissions: [],
    loading: false,
    modalVisible: false,
    modalType: 'group',
    editingGroup: null,
  });

  useEffect(() => {
    loadPermissionGroups();
    loadPrograms();
    loadUsers();
  }, []);

  const loadPermissionGroups = async () => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      const response = await UserService.listPermissionGroups();
      setState(prev => ({
        ...prev,
        permissionGroups: response.items,
        loading: false,
      }));
    } catch (error) {
      message.error('載入權限群組失敗');
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const loadPrograms = async () => {
    try {
      const programs = await UserService.listPrograms();
      console.log('Loaded programs:', programs);
      setState(prev => ({ ...prev, programs }));
    } catch (error: any) {
      // 使用 info 層級記錄，因為這對某些用戶來說是預期行為
      if (error?.response?.status === 403) {
        console.info('用戶沒有查看程式清單的權限，使用空清單');
      } else {
        console.warn('載入程式清單時發生錯誤:', error);
      }
      
      // 不顯示錯誤訊息，因為一般用戶可能沒有查看程式清單的權限
      // 但仍需要程式清單來顯示權限設定，所以使用空陣列
      setState(prev => ({ ...prev, programs: [] }));
      
      // 可以考慮從權限群組的權限設定中提取程式資訊
      // 或者提供一個公開的 API 來獲取基本的程式清單
    }
  };

  const loadUsers = async () => {
    try {
      const response = await UserService.listUsers({ limit: 1000 });
      setState(prev => ({ ...prev, users: response.items }));
    } catch (error) {
      message.error('載入使用者清單失敗');
    }
  };

  const loadGroupDetails = async (group: PermissionGroup) => {
    try {
      const groupDetails = await UserService.getPermissionGroup(group.rGroupId);
      console.log('Loaded group details:', groupDetails);
      console.log('Group permissions:', groupDetails.permissions);
      console.log('Group permissions length:', groupDetails.permissions?.length);
      console.log('First permission:', groupDetails.permissions?.[0]);
      
      // 建立完整的權限列表，包含所有程式
      const allPrograms = state.programs;
      const existingPermissions = groupDetails.permissions || [];
      
      // 為每個程式建立權限項目
      const fullPermissions = allPrograms.map(program => {
        const existing = existingPermissions.find(p => p.programId === program.pgId);
        if (existing) {
          console.log(`Found existing permission for program ${program.pgId}:`, existing);
          return existing;
        }
        // 如果沒有現有權限，建立預設權限（全部為 false）
        return {
          programId: program.pgId,
          programName: program.pgName,
          query: false,
          addNew: false,
          edit: false,
          delete: false,
          export: false,
          print: false,
          approve: false
        };
      });

      console.log('Full permissions list:', fullPermissions);
      
      setState(prev => ({
        ...prev,
        selectedGroup: groupDetails,
        groupMembers: groupDetails.members?.map(m => m.userId) || [],
        groupPermissions: fullPermissions,
      }));
    } catch (error) {
      console.error('載入群組詳情失敗:', error);
    }
  };

  const handleSelectGroup = (group: PermissionGroup) => {
    // 先清空之前的權限資料，避免顯示舊資料
    setState(prev => ({
      ...prev,
      groupPermissions: [],
      groupMembers: []
    }));
    loadGroupDetails(group);
  };

  const handleCreateGroup = () => {
    setState(prev => ({
      ...prev,
      modalVisible: true,
      modalType: 'group',
      editingGroup: null,
    }));
    form.resetFields();
  };

  const handleEditGroup = (group: PermissionGroup) => {
    setState(prev => ({
      ...prev,
      modalVisible: true,
      modalType: 'group',
      editingGroup: group,
    }));
    form.setFieldsValue(group);
  };

  const handleDeleteGroup = (group: PermissionGroup) => {
    Modal.confirm({
      title: '確認刪除權限群組',
      content: `確定要刪除權限群組 "${group.rGroupName}" 嗎？`,
      okText: '確定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          await UserService.deletePermissionGroup(group.rGroupId);
          message.success('權限群組刪除成功');
          loadPermissionGroups();
          if (state.selectedGroup?.rGroupId === group.rGroupId) {
            setState(prev => ({ ...prev, selectedGroup: null }));
          }
        } catch (error) {
          message.error(`刪除失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
        }
      },
    });
  };

  const handleToggleGroupStatus = async (groupId: string, isActive: boolean) => {
    try {
      await UserService.updatePermissionGroup(groupId, { isActive });
      message.success(`權限群組已${isActive ? '啟用' : '停用'}`);
      loadPermissionGroups();
    } catch (error) {
      message.error(`操作失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
    }
  };

  const handleEditMembers = () => {
    setState(prev => ({
      ...prev,
      modalVisible: true,
      modalType: 'members',
    }));
  };

  const handleEditPermissions = () => {
    setState(prev => ({
      ...prev,
      modalVisible: true,
      modalType: 'permissions',
    }));
  };

  const handleModalSubmit = async () => {
    try {
      if (state.modalType === 'group') {
        const values = await form.validateFields();
        
        if (state.editingGroup) {
          await UserService.updatePermissionGroup(state.editingGroup.rGroupId, values);
          message.success('權限群組更新成功');
        } else {
          await UserService.createPermissionGroup(values);
          message.success('權限群組建立成功');
        }
        
        loadPermissionGroups();
        setState(prev => ({ ...prev, modalVisible: false }));
      }
    } catch (error) {
      message.error(`操作失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
    }
  };

  // Permission group table columns
  const groupColumns: ColumnsType<PermissionGroup> = [
    {
      title: '群組名稱',
      dataIndex: 'rGroupName',
      key: 'rGroupName',
    },
    {
      title: '群組ID',
      dataIndex: 'rGroupId',
      key: 'rGroupId',
    },
    {
      title: '狀態',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean, record: PermissionGroup) => (
        <Switch
          checked={isActive}
          checkedChildren="啟用"
          unCheckedChildren="停用"
          onChange={(checked) => handleToggleGroupStatus(record.rGroupId, checked)}
        />
      ),
    },
    {
      title: '建立日期',
      dataIndex: 'createDate',
      key: 'createDate',
      render: (date: string) => date ? new Date(date).toLocaleDateString('zh-TW') : 'N/A',
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            size="small"
            onClick={() => handleSelectGroup(record)}
          >
            檢視
          </Button>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditGroup(record)}
          />
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteGroup(record)}
          />
        </Space>
      ),
    },
  ];

  // Convert programs to tree data for permission setting
  const getProgramTreeData = (programs: Program[]): DataNode[] => {
    return programs.map(program => ({
      key: program.pgId,
      title: (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{program.pgName}</span>
          <Space size="small">
            {Object.entries(PERMISSION_ACTIONS).map(([key, action]) => {
              const permission = state.groupPermissions.find(p => p.programId === program.pgId);
              const hasPermission = permission?.[action as keyof UserPermission] as boolean;
              
              return (
                <Checkbox
                  key={action}
                  checked={hasPermission}
                  onChange={(e) => handlePermissionChange(program.pgId, action as keyof UserPermission, e.target.checked)}
                  style={{ fontSize: '12px' }}
                >
                  {key.replace('_', '')}
                </Checkbox>
              );
            })}
          </Space>
        </div>
      ),
      children: program.children ? getProgramTreeData(program.children) : undefined,
    }));
  };

  // Filter programs by search text (keeps parent nodes if any child matches)
  const filterPrograms = (programs: Program[], search: string): Program[] => {
    if (!search) return programs;
    const lowerSearch = search.toLowerCase();
    return programs.reduce<Program[]>((acc, program) => {
      const nameMatches = program.pgName.toLowerCase().includes(lowerSearch);
      const filteredChildren = program.children ? filterPrograms(program.children, search) : [];
      if (nameMatches || filteredChildren.length > 0) {
        acc.push({
          ...program,
          children: nameMatches ? program.children : filteredChildren.length > 0 ? filteredChildren : program.children,
        });
      }
      return acc;
    }, []);
  };

  // Get transfer data for members
  const getTransferData = (): TransferItem[] => {
    return state.users.map(user => ({
      key: user.userId,
      title: `${user.userName} (${user.userId})`,
      description: user.department?.dptName || 'N/A',
    }));
  };

  // Handle permission change
  const handlePermissionChange = (programId: number, permissionType: keyof UserPermission, checked: boolean) => {
    console.log(`Permission change: programId=${programId}, type=${permissionType}, checked=${checked}`);
    
    setState(prev => {
      const newPermissions = [...prev.groupPermissions];
      const existingIndex = newPermissions.findIndex(p => p.programId === programId);
      
      console.log(`Existing permission index: ${existingIndex}`);
      
      if (existingIndex !== -1) {
        // Update existing permission
        newPermissions[existingIndex] = {
          ...newPermissions[existingIndex],
          [permissionType]: checked
        };
        console.log('Updated existing permission:', newPermissions[existingIndex]);
      } else {
        // Create new permission entry
        const newPermission: UserPermission = {
          programId,
          programName: '', // Will be filled later
          query: false,
          addNew: false,
          edit: false,
          delete: false,
          export: false,
          print: false,
          approve: false,
          [permissionType]: checked
        };
        newPermissions.push(newPermission);
        console.log('Created new permission:', newPermission);
      }
      
      console.log('Total permissions after change:', newPermissions.length);
      return { ...prev, groupPermissions: newPermissions };
    });
  };

  // Handle save group members
  const handleSaveGroupMembers = async () => {
    if (!state.selectedGroup) {
      message.error('請先選擇群組');
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true }));
      
      // 獲取原始成員列表
      const groupDetails = await UserService.getPermissionGroup(state.selectedGroup.rGroupId);
      const originalMemberIds = groupDetails.members?.map(m => m.userId) || [];
      
      // 計算需要新增和移除的成員
      const membersToAdd = state.groupMembers.filter(id => !originalMemberIds.includes(id));
      const membersToRemove = originalMemberIds.filter(id => !state.groupMembers.includes(id));
      
      // 執行新增和移除操作
      if (membersToAdd.length > 0) {
        await UserService.addGroupMembers(state.selectedGroup.rGroupId, membersToAdd);
      }
      
      if (membersToRemove.length > 0) {
        await UserService.removeGroupMembers(state.selectedGroup.rGroupId, membersToRemove);
      }
      
      message.success('群組成員更新成功');
      setState(prev => ({ ...prev, modalVisible: false }));
      
      // 重新載入群組詳情
      await handleSelectGroup(state.selectedGroup);
    } catch (error) {
      message.error(`儲存失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  // Handle save group permissions
  const handleSaveGroupPermissions = async () => {
    if (!state.selectedGroup) {
      message.error('請先選擇群組');
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true }));
      
      console.log('Saving permissions for group:', state.selectedGroup.rGroupId);
      console.log('Current groupPermissions state:', state.groupPermissions);
      console.log('All permissions before filter:', state.groupPermissions.map(p => ({
        programId: p.programId,
        hasAnyPermission: p.query || p.addNew || p.edit || p.delete || p.export || p.print,
        permissions: {
          query: p.query,
          addNew: p.addNew,
          edit: p.edit,
          delete: p.delete,
          export: p.export,
          print: p.print
        }
      })));
      
      // 儲存所有程式的權限設定（包括全部為 false 的）
      // 這樣可以確保清除之前設定的權限
      const permissionsToSave = state.groupPermissions;
      
      console.log('Filtered permissions to save:', permissionsToSave);
      console.log('Detailed permissions:', permissionsToSave.map(p => ({
        programId: p.programId,
        programName: p.programName,
        query: p.query,
        addNew: p.addNew,
        edit: p.edit,
        delete: p.delete,
        export: p.export,
        print: p.print
      })));
      
      // 更新群組權限
      await UserService.updateGroupPermissions(state.selectedGroup.rGroupId, permissionsToSave);
      
      message.success('群組權限更新成功');
      setState(prev => ({ ...prev, modalVisible: false }));
      await refreshPermissions();
      
      // 重新載入群組詳情
      console.log('Reloading group details after save...');
      await handleSelectGroup(state.selectedGroup);
    } catch (error) {
      console.error('Save permissions error:', error);
      message.error(`儲存失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>權限管理</Title>
          <Text type="secondary">管理權限群組和使用者權限</Text>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        {/* Permission Groups List */}
        <Col xs={24} lg={14}>
          <Card
            title={
              <Space>
                <TeamOutlined />
                <span>權限群組</span>
              </Space>
            }
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreateGroup}
              >
                新增群組
              </Button>
            }
          >
            <Table
              columns={groupColumns}
              dataSource={state.permissionGroups}
              rowKey="rGroupId"
              loading={state.loading}
              size="small"
              pagination={{ pageSize: 10 }}
              onRow={(record) => ({
                onClick: () => handleSelectGroup(record),
                style: { cursor: 'pointer' },
              })}
            />
          </Card>
        </Col>

        {/* Group Details */}
        <Col xs={24} lg={10}>
          <Card
            title={
              <Space>
                <SecurityScanOutlined />
                <span>群組詳情</span>
              </Space>
            }
          >
            {state.selectedGroup ? (
              <div>
                <Title level={5}>{state.selectedGroup.rGroupName}</Title>
                <Text type="secondary">群組ID: {state.selectedGroup.rGroupId}</Text>
                
                <div style={{ marginTop: '16px' }}>
                  <Space>
                    <Button
                      icon={<UserOutlined />}
                      onClick={handleEditMembers}
                    >
                      管理成員 ({state.groupMembers.length})
                    </Button>
                    <Button
                      icon={<SettingOutlined />}
                      onClick={handleEditPermissions}
                    >
                      設定權限 ({state.groupPermissions.length})
                    </Button>
                  </Space>
                </div>

                {state.selectedGroup.memo && (
                  <div style={{ marginTop: '16px' }}>
                    <Text strong>備註：</Text>
                    <div style={{ marginTop: '4px' }}>
                      <Text>{state.selectedGroup.memo}</Text>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Text type="secondary">請選擇一個權限群組查看詳情</Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Modal for Create/Edit Group */}
      <Modal
        title={`${state.editingGroup ? '編輯' : '新增'}權限群組`}
        open={state.modalVisible && state.modalType === 'group'}
        onOk={handleModalSubmit}
        onCancel={() => setState(prev => ({ ...prev, modalVisible: false }))}
        okText="確定"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="rGroupId"
            label="群組ID"
            rules={[{ required: true, message: '請輸入群組ID' }]}
          >
            <Input placeholder="輸入群組ID" disabled={!!state.editingGroup} />
          </Form.Item>
          <Form.Item
            name="rGroupName"
            label="群組名稱"
            rules={[{ required: true, message: '請輸入群組名稱' }]}
          >
            <Input placeholder="輸入群組名稱" />
          </Form.Item>
          <Form.Item name="memo" label="備註">
            <Input.TextArea placeholder="輸入備註" rows={3} />
          </Form.Item>
          <Form.Item name="isActive" label="狀態" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="啟用" unCheckedChildren="停用" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal for Managing Members */}
      <Modal
        title="管理群組成員"
        open={state.modalVisible && state.modalType === 'members'}
        onCancel={() => setState(prev => ({ ...prev, modalVisible: false }))}
        footer={[
          <Button key="cancel" onClick={() => setState(prev => ({ ...prev, modalVisible: false }))}>
            取消
          </Button>,
          <Button key="ok" type="primary" onClick={handleSaveGroupMembers} loading={state.loading}>
            儲存
          </Button>,
        ]}
        width={800}
      >
        <Transfer
          dataSource={getTransferData()}
          targetKeys={state.groupMembers}
          showSearch
          titles={['可用使用者', '群組成員']}
          render={(item) => item.title || ''}
          onChange={(targetKeys) => setState(prev => ({ ...prev, groupMembers: targetKeys as string[] }))}
        />
      </Modal>

      {/* Modal for Managing Permissions */}
      <Modal
        title="設定群組權限"
        open={state.modalVisible && state.modalType === 'permissions'}
        onCancel={() => setState(prev => ({ ...prev, modalVisible: false }))}
        footer={[
          <Button key="cancel" onClick={() => setState(prev => ({ ...prev, modalVisible: false }))}>
            取消
          </Button>,
          <Button key="ok" type="primary" onClick={handleSaveGroupPermissions} loading={state.loading}>
            儲存
          </Button>,
        ]}
        width={1000}
      >
        <Input.Search
          placeholder="搜尋程式名稱..."
          allowClear
          style={{ marginBottom: 12 }}
          onChange={e => setPermSearchText(e.target.value)}
        />
        <Tree
          treeData={getProgramTreeData(filterPrograms(state.programs, permSearchText))}
          showLine
          defaultExpandAll
          height={400}
        />
      </Modal>
    </div>
  );
};

export default PermissionManagement;